import { useEffect, useRef, useState, useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import JsBarcode from "jsbarcode";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Printer, Download, Loader2 } from "lucide-react";

/**
 * ShippingLabelDialog — production 4×6 in shipping label.
 *
 * Single-parcel orders render ONE label. Multi-parcel orders (Sprint 56)
 * render N stacked labels — one per parcel — each tagged "Parcel X of N"
 * so the courier can match physical boxes to the order. Print + PDF both
 * iterate every label so the customer gets the full set in one action.
 *
 * Each label includes: sender / receiver, QR (links to public tracking
 * URL), Code128 barcode of the tracking_id, payment-mode badge, the
 * parcel-specific name + weight + dimensions, and PickNGo Swift header.
 *
 * If a field is missing on the order, "N/A" renders — never crash.
 */
const TRACK_BASE = "https://www.pickngo.co.in/track/";

const fallback = (v) => (v === null || v === undefined || v === "" ? "N/A" : v);

function fmtDate(iso) {
  if (!iso) return "N/A";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return "N/A"; }
}

function paymentLabel(order) {
  const pm = (order?.payment_method || "").toLowerCase();
  const ps = (order?.payment_status || "").toLowerCase();
  if (pm === "cod" || ps === "cod") return "COD";
  if (ps === "paid" || pm === "razorpay" || pm === "wallet" || pm === "online") return "PREPAID";
  return "PREPAID";
}

/**
 * Build the list of parcels to render. For multi-parcel orders we use
 * `order.items` (the saved per-parcel breakdown). For single-parcel
 * orders we synthesize ONE entry from the legacy single-shipment fields
 * so the label component itself doesn't need a special-case render path.
 */
function parcelsFor(order) {
  const items = order?.items || [];
  if (items.length > 0) {
    return items.map((it, i) => ({
      idx: i,
      item_id: it.item_id || `p-${i}`,
      item_name: it.item_name || `Parcel #${i + 1}`,
      weight_kg: it.weight_kg,
      length_cm: it.length_cm,
      width_cm: it.width_cm,
      height_cm: it.height_cm,
      quantity: it.quantity || 1,
    }));
  }
  return [{
    idx: 0,
    item_id: "single",
    item_name: order?.package_description || "Parcel",
    weight_kg: order?.package_weight,
    length_cm: order?.length_cm,
    width_cm: order?.width_cm,
    height_cm: order?.height_cm,
    quantity: 1,
  }];
}

export default function ShippingLabelDialog({ open, onOpenChange, order }) {
  const labelsContainerRef = useRef(null);
  const barcodeRefs = useRef({});
  const [pdfBusy, setPdfBusy] = useState(false);

  const tid = order?.tracking_id || "N/A";
  const trackUrl = `${TRACK_BASE}${tid}`;
  const parcels = useMemo(() => parcelsFor(order), [order]);
  const totalParcels = parcels.length;

  // Render a barcode SVG for every parcel (each label needs its own DOM
  // node, so we keep one ref per parcel index).
  useEffect(() => {
    if (!open || !tid || tid === "N/A") return;
    const handle = requestAnimationFrame(() => {
      parcels.forEach((p) => {
        const node = barcodeRefs.current[p.idx];
        if (!node) return;
        try {
          JsBarcode(node, tid, {
            format: "CODE128",
            displayValue: false,
            margin: 0,
            height: 56,
            width: 1.6,
            background: "#ffffff",
            lineColor: "#000000",
          });
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[label] barcode render failed:", e);
        }
      });
    });
    return () => cancelAnimationFrame(handle);
  }, [open, tid, parcels]);

  const handlePrint = () => {
    setTimeout(() => window.print(), 80);
  };

  const handleDownloadPdf = async () => {
    if (!labelsContainerRef.current) return;
    setPdfBusy(true);
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      // For multi-parcel orders we pass the whole stack — html2pdf paginates
      // each .label-printable as its own 4×6 page.
      await html2pdf()
        .set({
          margin: 0,
          filename: totalParcels > 1
            ? `${tid}-shipping-labels-${totalParcels}.pdf`
            : `${tid}-shipping-label.pdf`,
          jsPDF: { unit: "in", format: [4, 6], orientation: "portrait" },
          html2canvas: { scale: 3, useCORS: true, backgroundColor: "#ffffff" },
          image: { type: "jpeg", quality: 0.98 },
          pagebreak: { mode: ["css", "legacy"], before: ".label-page-break" },
        })
        .from(labelsContainerRef.current)
        .save();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[label] pdf download failed:", e);
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[460px] max-h-[95vh] overflow-y-auto p-0 print:shadow-none print:p-0 print:max-w-none print:max-h-none"
        data-testid="shipping-label-dialog"
      >
        <DialogHeader className="p-4 pb-2 print:hidden">
          <DialogTitle className="font-['Outfit'] text-lg font-bold flex items-center gap-2">
            <Printer className="h-5 w-5 text-[#FF6B00]" />
            Shipping Label{totalParcels > 1 ? `s (${totalParcels})` : ""} · {tid}
          </DialogTitle>
          <DialogDescription>
            {totalParcels > 1
              ? `4×6 inch thermal-printer-ready · prints ${totalParcels} labels (one per parcel).`
              : "4×6 inch thermal-printer-ready. Auto-filled from the order."}
          </DialogDescription>
        </DialogHeader>

        {/* ── PRINT-SCOPED CSS ──────────────────────────────────────────── */}
        <style>{`
          @media print {
            @page { size: 4in 6in; margin: 0; }
            html, body { background: #fff !important; margin: 0 !important; padding: 0 !important; }
            body * { visibility: hidden !important; }
            .label-printable, .label-printable * { visibility: visible !important; }
            .label-printable {
              position: relative !important;
              width: 4in !important; height: 6in !important;
              margin: 0 !important; padding: 0 !important;
              box-shadow: none !important;
              border: none !important;
              page-break-after: always;
              break-after: page;
            }
            .label-stack {
              position: fixed !important;
              top: 0 !important; left: 0 !important;
            }
          }
        `}</style>

        {/* ── LABEL STACK — one .label-printable per parcel ──────────── */}
        <div
          ref={labelsContainerRef}
          className="label-stack px-4 print:px-0"
          data-testid="shipping-label-stack"
        >
          {parcels.map((parcel, i) => (
            <div
              key={parcel.item_id}
              className={`${i > 0 ? "mt-4 label-page-break" : ""}`}
            >
              <SingleLabel
                order={order}
                tid={tid}
                trackUrl={trackUrl}
                parcel={parcel}
                parcelIndex={parcel.idx + 1}
                totalParcels={totalParcels}
                barcodeRefSetter={(node) => { barcodeRefs.current[parcel.idx] = node; }}
              />
            </div>
          ))}
        </div>

        {/* ── ACTION BAR ────────────────────────────────────────────── */}
        <div className="flex items-center justify-end gap-2 p-4 pt-2 border-t border-slate-100 print:hidden">
          <Button
            variant="outline"
            onClick={handleDownloadPdf}
            disabled={pdfBusy}
            className="rounded-xl"
            data-testid="label-download-pdf-btn"
          >
            {pdfBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
            {pdfBusy ? "Building PDF…" : totalParcels > 1 ? `Download ${totalParcels} PDFs` : "Download PDF"}
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl"
            data-testid="label-print-btn"
          >
            <Printer className="h-4 w-4 mr-1.5" /> {totalParcels > 1 ? `Print ${totalParcels} Labels` : "Print Label"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ── Single 4×6 label — re-used for every parcel in a multi-parcel order. ── */
function SingleLabel({ order, tid, trackUrl, parcel, parcelIndex, totalParcels, barcodeRefSetter }) {
  const isMulti = totalParcels > 1;
  return (
    <div
      data-testid={`shipping-label-printable-${parcelIndex}`}
      className="label-printable bg-white text-slate-900"
      style={{
        width: "384px",
        height: "576px",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        border: "1px solid #000",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        padding: "10px 12px",
      }}
    >
      {/* HEADER ── Logo + brand + payment badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "2px solid #000", paddingBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img
            src="/pickngo-logo.jpeg"
            alt="PickNGo Swift"
            width={32}
            height={32}
            style={{ borderRadius: 6, objectFit: "contain", background: "#fff", border: "1px solid #ddd" }}
            crossOrigin="anonymous"
          />
          <div style={{ lineHeight: 1.05 }}>
            <div style={{ fontWeight: 800, fontSize: 16, letterSpacing: -0.3 }}>
              Pick<span style={{ color: "#FF6B00" }}>N</span>Go <span style={{ fontWeight: 700, opacity: 0.85 }}>Swift</span>
            </div>
            <div style={{ fontSize: 9, color: "#475569", fontWeight: 600 }}>www.pickngo.co.in</div>
          </div>
        </div>
        <div
          data-testid="label-payment-badge"
          style={{
            fontSize: 12,
            fontWeight: 800,
            padding: "4px 8px",
            border: "2px solid #000",
            borderRadius: 4,
            letterSpacing: 0.5,
            background: paymentLabel(order) === "COD" ? "#000" : "#fff",
            color: paymentLabel(order) === "COD" ? "#fff" : "#000",
          }}
        >
          {paymentLabel(order)}
        </div>
      </div>

      {/* PARCEL-OF-N pill ── only renders for multi-parcel orders so the
          single-parcel layout stays identical to pre-Sprint 56. */}
      {isMulti && (
        <div
          data-testid={`label-parcel-of-${parcelIndex}`}
          style={{
            marginTop: 6,
            padding: "4px 8px",
            background: "#FFF4E6",
            border: "1px solid #FF6B00",
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 800,
            color: "#FF6B00",
            letterSpacing: 0.5,
            textAlign: "center",
          }}
        >
          PARCEL {parcelIndex} of {totalParcels} · {parcel.item_name}
        </div>
      )}

      {/* PICKUP / FROM */}
      <LabelBlock
        label="FROM (Pickup)"
        testId="label-pickup"
        name={fallback(order?.pickup_contact_name || order?.user_name)}
        phone={fallback(order?.pickup_phone || order?.user_phone)}
        house={order?.pickup_house_no}
        street={order?.pickup_street}
        flat={order?.pickup_flat_no}
        address={fallback(order?.pickup_address)}
      />

      {/* DROP / TO */}
      <LabelBlock
        label="TO (Deliver)"
        testId="label-drop"
        name={fallback(order?.delivery_contact_name)}
        phone={fallback(order?.delivery_phone)}
        house={order?.delivery_house_no}
        street={order?.delivery_street}
        flat={order?.delivery_flat_no}
        address={fallback(order?.delivery_address)}
        emphasis
      />

      {/* META row — Order ID · Date · Weight (parcel-specific) */}
      <div
        data-testid={`label-meta-${parcelIndex}`}
        style={{
          display: "grid",
          gridTemplateColumns: isMulti ? "1.2fr 1fr 0.9fr 1.1fr" : "1fr 1fr 1fr",
          gap: 4,
          borderTop: "1px dashed #000",
          borderBottom: "1px dashed #000",
          padding: "5px 0",
          marginTop: 5,
          fontSize: 9,
        }}
      >
        <Meta label="ORDER ID" value={tid} mono testId="label-meta-tid" />
        <Meta label="DATE" value={fmtDate(order?.created_at)} testId="label-meta-date" />
        <Meta
          label="WEIGHT"
          value={parcel.weight_kg != null ? `${parcel.weight_kg} kg` : "N/A"}
          testId="label-meta-weight"
        />
        {isMulti && (
          <Meta
            label="DIMENSIONS"
            value={
              (parcel.length_cm && parcel.width_cm && parcel.height_cm)
                ? `${parcel.length_cm}×${parcel.width_cm}×${parcel.height_cm} cm`
                : "—"
            }
            testId="label-meta-dims"
          />
        )}
      </div>

      {/* CODES row — barcode + QR */}
      <div
        data-testid="label-codes"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 6,
          flex: 1,
        }}
      >
        <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <svg ref={barcodeRefSetter} data-testid={`label-barcode-${parcelIndex}`} />
          <div style={{ fontSize: 9, fontFamily: "ui-monospace, monospace", marginTop: 2, fontWeight: 700, letterSpacing: 1 }}>
            {tid}
            {isMulti && <span style={{ marginLeft: 4, color: "#FF6B00" }}>· P{parcelIndex}/{totalParcels}</span>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "2px 4px", border: "1px solid #000", borderRadius: 4 }}>
          <QRCodeSVG
            value={trackUrl}
            size={84}
            level="M"
            includeMargin={false}
            data-testid="label-qr"
          />
          <div style={{ fontSize: 7, color: "#0f172a", marginTop: 2, fontWeight: 700, textAlign: "center", lineHeight: 1.1 }}>
            SCAN TO TRACK
          </div>
        </div>
      </div>

      {/* HANDLING flags */}
      <div
        data-testid="label-flags"
        style={{
          marginTop: 6,
          display: "flex",
          gap: 4,
          fontSize: 9,
          fontWeight: 800,
        }}
      >
        <Flag label="⚠ HANDLE WITH CARE" />
        <Flag label="📦 OPEN BOX OK" subtle />
      </div>

      {/* FOOTER */}
      <div style={{ marginTop: 4, paddingTop: 4, borderTop: "1px solid #000", fontSize: 8, color: "#475569", textAlign: "center", lineHeight: 1.3 }}>
        For support: pickngo13@gmail.com · +91 79958 65243
        <br />
        Track: {trackUrl}
      </div>
    </div>
  );
}

/* ── Sub-components (inline-styled so PDF rendering matches the print) ── */

function LabelBlock({ label, name, phone, address, testId, emphasis, house, street, flat }) {
  const sub = (v) => (v && String(v).trim() ? String(v).trim() : "—");
  return (
    <div data-testid={testId} style={{ marginTop: 6, lineHeight: 1.25 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: "#475569", letterSpacing: 1 }}>{label}</div>
      <div style={{ fontWeight: emphasis ? 800 : 700, fontSize: emphasis ? 14 : 13, marginTop: 1 }}>
        {name}
      </div>
      <div style={{ fontSize: 11, fontFamily: "ui-monospace, monospace", fontWeight: 600, color: "#000" }}>
        {phone}
      </div>
      <div style={{ fontSize: 9, color: "#0f172a", marginTop: 2, lineHeight: 1.35 }}>
        <span style={{ fontWeight: 700, color: "#475569" }}>House: </span>
        <span data-testid={`${testId}-house`}>{sub(house)}</span>
        <span style={{ color: "#94a3b8", margin: "0 4px" }}>·</span>
        <span style={{ fontWeight: 700, color: "#475569" }}>St: </span>
        <span data-testid={`${testId}-street`}>{sub(street)}</span>
        <span style={{ color: "#94a3b8", margin: "0 4px" }}>·</span>
        <span style={{ fontWeight: 700, color: "#475569" }}>Flat: </span>
        <span data-testid={`${testId}-flat`}>{sub(flat)}</span>
      </div>
      <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }} data-testid={`${testId}-full`}>
        {address}
      </div>
    </div>
  );
}

function Meta({ label, value, mono, testId }) {
  return (
    <div data-testid={testId}>
      <div style={{ fontSize: 7, color: "#475569", fontWeight: 800, letterSpacing: 0.6 }}>{label}</div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 800,
          color: "#000",
          fontFamily: mono ? "ui-monospace, monospace" : undefined,
          wordBreak: "break-word",
        }}
      >
        {value}
      </div>
    </div>
  );
}

function Flag({ label, subtle }) {
  return (
    <div
      style={{
        flex: 1,
        textAlign: "center",
        padding: "3px 4px",
        border: "1px solid #000",
        borderRadius: 3,
        background: subtle ? "#fff" : "#000",
        color: subtle ? "#000" : "#fff",
        whiteSpace: "nowrap",
        overflow: "hidden",
      }}
    >
      {label}
    </div>
  );
}
